import {useEffect, useRef, useState} from 'react';
// eslint-disable-next-line no-restricted-imports
import {useSearchParams} from 'react-router-dom';

import {isNilOrEmpty} from 'ramda-adjunct';

import {useBoolean, useOnMount} from 'shared/web';

import {GetDataIdsByQuery} from '../types/Api';
import {getFormattedSourceSearchParams} from '../utils/url/getFormattedSourceSearchParams';
import {setSourceSearchParamsByGridCode} from '../utils/url/setSourceSearchParamsByGridCode';
import {useHttpCalls} from './useHttpCalls';

const DETAIL_PAGINATION_LIMIT = 50;
export function useDetailPagination(
  onChange: (entityIndex: string, sourceSearchParams: string) => void
) {
  const [searchParams] = useSearchParams();

  // TODO: Optimize fetchIds - implement cache
  // Problems:
  // 1. RowControl: fetches on every page change, even if ID is already in ids.current
  // 2. EntityHeaderNavigation: loses ids on unmount
  // https://carvago.atlassian.net/browse/T20-107385
  const ids = useRef<GetDataIdsByQuery[]>(null);

  const [page, setPage] = useState(Number(getFormattedSourceSearchParams().rowIndex));
  const [isInvalid, invalidate, validate] = useBoolean();
  const [isLoading, startLoading, stopLoading] = useBoolean();

  const http = useHttpCalls({gridCode: getFormattedSourceSearchParams().gridCode});

  useOnMount(() => {
    fetchIds();
  });

  useEffect(() => {
    const newPage = getFormattedSourceSearchParams().rowIndex;
    setPage(Number(newPage));
  }, [searchParams]);

  const onPageChange = (newPage: number) => {
    const foundEntityId = ids.current?.find((id) => id.rowNumber === newPage)?.id;

    if (isNilOrEmpty(foundEntityId)) {
      invalidate();
      return;
    }

    const sourceSearchParams = setSourceSearchParamsByGridCode(
      getFormattedSourceSearchParams().gridCode,
      String(newPage),
      true
    );

    const initialPath = window.location.pathname + window.location.search;

    onChange(foundEntityId as string, sourceSearchParams);

    const currentPathAfter = window.location.pathname + window.location.search;
    const wasNavigationBlocked = currentPathAfter === initialPath;

    if (wasNavigationBlocked) {
      return;
    }

    validate();
    setPage(newPage);
    fetchIds();
  };

  const fetchIds = () => {
    const offset = Number(page) - Math.round(DETAIL_PAGINATION_LIMIT / 2);

    startLoading();
    http
      .getDataIdsByQuery(
        getFormattedSourceSearchParams().queryId,
        offset > 0 ? offset : 0,
        DETAIL_PAGINATION_LIMIT,
        false
      )
      .then((response) => {
        ids.current = response ?? [];
      })
      .finally(() => {
        stopLoading();
      });
  };

  return {
    rowIndex: page,
    ids,
    onPageChange,
    isInvalid,
    isLoading,
  };
}
